import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  TextField,
  Alert,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/** Silme sırasında hangi aşamada olduğumuz — ilerleme çubuğunun etiketi buna göre değişir. */
export type DeleteAllPhase = 'products' | 'categories';

export interface DeleteAllProductsResult {
  deletedProducts: number;
  deletedCategories: number;
}

interface DeleteAllProductsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (includeCategories: boolean) => void;
  /** Ürün/kategori sayıları sunucudan çekilirken true */
  counting: boolean;
  productCount: number;
  categoryCount: number;
  deleting: boolean;
  phase: DeleteAllPhase | null;
  processedCount: number;
  phaseTotal: number;
  result: DeleteAllProductsResult | null;
}

const REQUIRED_TEXT = 'TÜM ÜRÜNLERİ SİL';

export const DeleteAllProductsDialog: React.FC<DeleteAllProductsDialogProps> = ({
  open,
  onClose,
  onConfirm,
  counting,
  productCount,
  categoryCount,
  deleting,
  phase,
  processedCount,
  phaseTotal,
  result,
}) => {
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [includeCategories, setIncludeCategories] = useState<boolean>(false);

  // Türkçe'de i/ı dönüşümü farklı olduğu için locale'li karşılaştırma şart:
  // varsayılan toUpperCase() "tüm ürünleri sil" metnini beklenen sonuca çevirmez.
  const normalized = confirmationText.trim().replace(/\s+/g, ' ').toLocaleUpperCase('tr-TR');
  const isConfirmed = normalized === REQUIRED_TEXT;

  useEffect(() => {
    if (!open) {
      setConfirmationText('');
      setIncludeCategories(false);
    }
  }, [open]);

  const handleConfirm = () => {
    if (isConfirmed && !deleting) {
      onConfirm(includeCategories);
    }
  };

  const progress = phaseTotal > 0 ? Math.round((processedCount / phaseTotal) * 100) : 0;
  const nothingToDelete = !counting && productCount === 0 && (!includeCategories || categoryCount === 0);

  // Sonuç ekranı: işlem bitti, özet gösteriliyor.
  if (result) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
          Silme İşlemi Tamamlandı
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Seçilen kayıtlar veritabanından kalıcı olarak silindi.
          </Alert>
          <Typography variant="body2">
            • Silinen ürün: <strong>{result.deletedProducts} adet</strong>
          </Typography>
          {result.deletedCategories > 0 && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              • Silinen kategori: <strong>{result.deletedCategories} adet</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} variant="contained" color="primary">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={!deleting ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningAmberIcon />
        Tüm Ürünleri Sil
      </DialogTitle>

      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            🚨 KRİTİK: Bu işlem geri alınamaz!
          </Typography>
        </Alert>

        <DialogContentText sx={{ mb: 2 }}>
          {counting ? (
            'Silinecek kayıtlar hesaplanıyor...'
          ) : (
            <>
              Şirketinizdeki{' '}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                toplam {productCount} ürünün tamamı
              </Box>{' '}
              veritabanından kalıcı olarak silinecektir.
            </>
          )}
        </DialogContentText>

        <Box sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="error.main">
            ⚠️ Bu işlemin sonuçları:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • Ekrandaki filtreler ve depo seçimi <strong>dikkate alınmaz</strong>, tüm ürünler silinir
          </Typography>
          <Typography variant="body2">
            • Ürünlerin <strong>stok hareketleri ve fiyat geçmişi</strong> de silinir
          </Typography>
          <Typography variant="body2">
            • Kayıtlar veritabanından tamamen kaldırılır, <strong>geri getirilemez</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Geçmiş sipariş ve faturalar silinmez, yalnızca ürün bağlantıları kopar
          </Typography>
        </Box>

        {!deleting && (
          <>
            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={includeCategories}
                  onChange={(e) => setIncludeCategories(e.target.checked)}
                  color="error"
                  disabled={counting}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Kategorileri de sil
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {counting
                      ? 'Kategori sayısı hesaplanıyor...'
                      : `Şirketteki ${categoryCount} kategori de kalıcı olarak silinir`}
                  </Typography>
                </Box>
              }
            />

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" fontWeight="bold" color="error.main" gutterBottom>
                İşlemi onaylamak için aşağıdaki kutuya büyük harflerle
                "<strong>{REQUIRED_TEXT}</strong>" yazın:
              </Typography>
              <TextField
                fullWidth
                autoFocus
                variant="outlined"
                placeholder={`${REQUIRED_TEXT} yazın`}
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                disabled={deleting || counting}
                sx={{ mt: 1 }}
                error={confirmationText.length > 0 && !isConfirmed}
                helperText={
                  confirmationText.length > 0 && !isConfirmed
                    ? `Lütfen tam olarak "${REQUIRED_TEXT}" yazın`
                    : isConfirmed
                    ? '✓ Onay metni doğru'
                    : ''
                }
                inputProps={{
                  style: { letterSpacing: '1px', fontWeight: 'bold' }
                }}
              />
            </Box>
          </>
        )}

        {deleting && (
          <Box sx={{ width: '100%', mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              {phase === 'categories' ? 'Kategoriler siliniyor...' : 'Ürünler siliniyor...'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {processedCount} / {phaseTotal}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant="determinate" value={progress} color="error" />
              </Box>
              <Box sx={{ minWidth: 40 }}>
                <Typography variant="body2" color="text.secondary">{`${progress}%`}</Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Bu pencereyi kapatmayın.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="primary" disabled={deleting} variant="outlined">
          İptal
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={deleting || counting || !isConfirmed || nothingToDelete}
          startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
        >
          {deleting
            ? 'Siliniyor...'
            : nothingToDelete
            ? 'Silinecek kayıt yok'
            : productCount === 0 && includeCategories
            ? `${categoryCount} Kategoriyi Kalıcı Sil`
            : `${productCount} Ürünü Kalıcı Sil`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
